import React from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { IonApp, IonIcon, IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, setupIonicReact, useIonAlert } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';

import { func, axius, images, env, types } from 'utils';
import { changeAuth, changeData, setAuthenticated } from 'store/actions';
import { Loading, Location } from 'components';

// ::: plugins
import OneSignal from 'onesignal-cordova-plugin';
import { StatusBar, Style } from "@capacitor/status-bar";

import 'assets/scss/custom.scss';

// :: screens
import Login from './pages/Auth/Login';
import Reset from './pages/Auth/Reset';
import User from './pages/User/_User';
import Orders from './pages/Orders/Orders';
import Ratings from './pages/Ratings';
import OrdersCancel from './pages/Orders/OrdersCancel';
import OrdersDetails from './pages/Orders/OrdersDetails';
import CouponHistory from './pages/Coupon/CouponHistory';

setupIonicReact();

const App: React.FC = () => {
  const store = useSelector((e: types.StateProps) => e);

  const dispatch = useDispatch();
  const history = useLocation();
  const [presentAlert] = useIonAlert();

  const [loading, setLoading] = React.useState(true);

  const { _auth: { user, token, isLoggedIn }, _data: { location } } = store;

  React.useEffect(() => {
    setupPlugins();
    getUserData();
    getSettings();

    // ::: get rider's currenct location
    getMyCurrentLocation();
    const clInterval = setInterval(() => {
      getMyCurrentLocation();
    }, 30 * 1000); // 30 secs

    const usInterval = setInterval(() => {
      getSettings();
      getUserData();
    }, 10 * 60 * 1000); // 10 mins

    return () => {
      clearInterval(usInterval);
      clearInterval(clInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setupOnesignal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setupOnesignal = async () => {
    if (func.isMobile) {
      OneSignal.setAppId(env.ONESIGNAL_ID);
      OneSignal.promptForPushNotificationsWithUserResponse(true, (granted) => {
        if (granted) {
          if (user && user.id) {
            OneSignal.getDeviceState((state) => {
              if (!state?.subscribed || !state.userId) {
                setTimeout(() => setupOnesignal(), 2000);
                return;
              }
              OneSignal.setEmail(user.email);
              OneSignal.setSMSNumber(user.phoneNumber);
              OneSignal.setExternalUserId(user.id);
              OneSignal.sendTags({ platform: func.config.platform, type: 'RIDER', version: func.app.version });
            });
          }
        }
      });
    }
  };

  const setupPlugins = async () => {
    if (func.isMobile) {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#000000' });
      await StatusBar.setStyle({ style: Style.Dark });
    }
  }

  const getUserData = () => {
    if (user.id) {
      axius.get(`users/me`).then(res => {
        if (res.status === 200) {
          dispatch(setAuthenticated({ user: res.data, token, }));
          if (res.data.status === 'INACTIVE') {
            axius.signout();
          }
        }
      });
    }
  }

  const getSettings = () => {
    axius.get('settings').then(res => {
      res.status === 200 && dispatch(changeData('settings', res.data));
      const oldVersion = func.versionBreak(func.app.version);
      const newVersion = func.versionBreak(res.data?.version?.rider);
      if (newVersion > oldVersion) {
        if (func.isMobile) {
          presentAlert({
            header: `Update your application`,
            message: `A brand new version of the "Republic Dash Rider" app is available in the Play Store. Please update your app to have a better experience.`,
            buttons: [
              {
                text: `Update now`,
                handler: () =>
                  func.redirect(
                    `https://play.google.com/store/apps/details?id=com.republic.dash.rider`
                  ),
              },
            ],
            backdropDismiss: false,
          });
        } else {
          caches.keys().then(keys => {
            Promise.all(keys.map(key => caches.delete(key))).then(() => {
              func.redirect('./');
            });
          });
        }
      }
      setLoading(false);
    });
  }

  const getMyCurrentLocation = () => {
    func.myCurrentLocation().then(geo => {
      dispatch(changeData('location', geo));
    });
  }

  if (loading) {
    return <Loading />;
  }

  if (!location?.x && !func.isLocal) {
    return <Location getMyCurrentLocation={getMyCurrentLocation} />;
  }

  const role = user?.role?.id;
  const routes = [
    { path: '/login', component: Login, exact: false, auth: false, roles: '*', },
    { path: '/reset', component: Reset, exact: false, auth: false, roles: '*', },

    { path: '/orders', icon: images.Requests, icon2: images.RequestsRed, name: 'Deliveries', component: Orders, roles: '*', tab: true, auth: true, },
    { path: '/ratings', icon: images.Ratings, icon2: images.RatingsRed, name: 'Ratings', component: Ratings, roles: '*', tab: true, auth: true, },
    { path: '/user', icon: images.Profile, icon2: images.ProfileRed, name: 'Profile', component: User, roles: '*', tab: true, auth: true, },
    // { path: '/help', icon: images.Help, icon2: images.HelpRed, name: 'Help', component: User, roles: '*', tab: true, auth: true, },


    { path: '/scan/history', name: 'Scan History', component: CouponHistory, roles: '*', tab: false, auth: true, },
    { path: '/orders/:id', name: 'OrdersDetails', component: OrdersDetails, roles: '*', tab: false, auth: true, },
    { path: '/orders-cancel', name: 'OrdersCancel', component: OrdersCancel, roles: '*', tab: false, auth: true, },
  ].filter(rt => rt.auth === isLoggedIn).filter((rt) => rt.roles === '*' || (rt.roles).includes(role));

  return (
    <IonApp>
      <IonTabs>
        <IonRouterOutlet>
          {routes.map((route) => (
            <Route key={route.path} path={route.path} exact={true} render={p => (
              <route.component {...p} {...store} dispatch={dispatch} changeData={changeData} changeAuth={changeAuth} setAuthenticated={setAuthenticated} />
            )} />
          ))}
          <Route exact path="/" render={() => <Redirect to={routes[0].path} />} />
          <Redirect to={routes[0].path} />
        </IonRouterOutlet>

        <IonTabBar slot="bottom" className={!isLoggedIn ? 'hide' : ''}>
          {routes.filter(tab => tab.tab).map(tab => (
            <IonTabButton key={tab.path} tab={tab.path.replace('/', '')} href={tab.path}>
              <IonIcon icon={history.pathname.split('/')[1] === tab.path.split('/')[1] ? tab.icon2 : tab.icon} />
              <IonLabel className={history.pathname.split('/')[1] === tab.path.split('/')[1] ? 'primary' : ''}>{tab.name}</IonLabel>
            </IonTabButton>
          ))}
        </IonTabBar>
      </IonTabs>
    </IonApp>
  );
}

export default App;
